import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  useSalonAppearanceApplyMutation,
  useSalonAppearanceResetMutation,
  useSalonAppearanceRetrieveQuery,
  useSalonAppearanceTargetsRetrieveQuery,
  useSalonAppearanceUpdateMutation,
} from '../../../services/salonsApi';
import {
  DEFAULT_SALON_PUBLIC_THEME,
  validateSalonPublicThemeDraft,
} from '../../../theme/salonPublicTheme';
import {
  DEFAULT_SALON_CONTENT_STYLE,
  DEFAULT_SALON_PUBLIC_LAYOUT,
  DEFAULT_SALON_TITLE_STYLE,
  SALON_SECTION_VISIBILITY_KEYS,
  SALON_SECTION_STYLE_KEYS,
  SALON_TEXT_STYLE_FIELDS,
  resolveSalonPublicLayout,
  validateSalonPublicLayoutDraft,
} from '../../../theme/salonPublicLayout';
import {
  DEFAULT_SALON_PUBLIC_TEMPLATE_KEY,
  createSalonTemplateAppearance,
  getSalonPublicTemplate,
} from '../../../theme/salonPublicTemplates';
import { resolveSalonThemeTargetIds } from './SalonThemeApplyScope';
import { SALON_THEME_COLOR_FIELDS } from './SalonThemeColorControls';
import SalonThemeActionBar from './SalonThemeActionBar';
import SalonThemeAdvancedControls, {
  getAdvancedGroupForField,
} from './SalonThemeAdvancedControls';
import SalonThemeDecisionDialog from './SalonThemeDecisionDialog';
import SalonThemeEssentialControls from './SalonThemeEssentialControls';
import SalonThemeFeedbackToast from './SalonThemeFeedbackToast';
import SalonThemeMobilePreviewSummary from './SalonThemeMobilePreviewSummary';
import SalonThemePreview from './SalonThemePreview';
import SalonThemeStudioLayout from './SalonThemeStudioLayout';
import SalonThemeViewportToggle from './SalonThemeViewportToggle';
import useSalonThemePreviewData from './useSalonThemePreviewData';


const BUTTON_STYLE_FIELDS = [
  'background_color',
  'border_color',
  'text_color',
  'font_key',
  'text_size',
  'weight',
  'italic',
  'transform',
  'letter_spacing',
];

const FIELD_ORDER = [
  'template_key',
  ...SALON_THEME_COLOR_FIELDS,
  'font_key',
  'buttons.appearance',
  'buttons.shape',
  'buttons.size',
  ...BUTTON_STYLE_FIELDS.map((field) => `buttons.${field}`),
  'header.composition',
  'header.surface',
  'header.behavior',
  'content.alignment',
  'content.services',
  'content.service_columns',
  'content.sidebar',
  ...SALON_SECTION_VISIBILITY_KEYS.map((field) => `visibility.${field}`),
  ...SALON_SECTION_STYLE_KEYS.flatMap((sectionKey) => (
    ['title', 'content'].flatMap((role) => (
      SALON_TEXT_STYLE_FIELDS.map((field) => (
        `section_styles.${sectionKey}.${role}.${field}`
      ))
    ))
  )),
];

const cloneLayout = (layout = DEFAULT_SALON_PUBLIC_LAYOUT) => (
  resolveSalonPublicLayout(layout)
);

const createDefaultAppearance = () => createSalonTemplateAppearance(
  DEFAULT_SALON_PUBLIC_TEMPLATE_KEY,
);

const cloneAppearance = (appearance = createDefaultAppearance()) => ({
  template_key: getSalonPublicTemplate(appearance.template_key).key,
  theme: { ...(appearance.theme || DEFAULT_SALON_PUBLIC_THEME) },
  layout: cloneLayout(appearance.layout || DEFAULT_SALON_PUBLIC_LAYOUT),
});

const firstError = (error) => (
  Array.isArray(error) ? error[0] : error
);

const flattenFieldErrors = (errors, prefix = '') => {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return prefix ? { [prefix]: firstError(errors) } : {};
  }
  return Object.fromEntries(Object.entries(errors).flatMap(([field, value]) => {
    if (field === 'non_field_errors' || field === 'unknown_fields') return [];
    const path = prefix ? `${prefix}.${field}` : field;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return Object.entries(flattenFieldErrors(value, path));
    }
    return [[path, firstError(value)]];
  }));
};

const findNonFieldError = (errors) => {
  if (!errors || typeof errors !== 'object') return '';
  const directError = firstError(errors.non_field_errors);
  if (directError) return directError;
  for (const value of Object.values(errors)) {
    const nestedError = findNonFieldError(value);
    if (nestedError) return nestedError;
  }
  return '';
};

const SalonThemeEditor = ({ salonId, salonName }) => {
  const { t, i18n } = useTranslation();
  const previewData = useSalonThemePreviewData(salonId, i18n.language);
  const appearanceQuery = useSalonAppearanceRetrieveQuery(salonId);
  const targetsQuery = useSalonAppearanceTargetsRetrieveQuery({
    salonId,
    language: i18n.language,
  });
  const [updateAppearance, updateState] = useSalonAppearanceUpdateMutation();
  const [applyAppearance, applyState] = useSalonAppearanceApplyMutation();
  const [resetAppearance, resetState] = useSalonAppearanceResetMutation();
  const [draft, setDraft] = useState(createDefaultAppearance);
  const [baseline, setBaseline] = useState(createDefaultAppearance);
  const [fieldErrors, setFieldErrors] = useState({
    template_key: '',
    theme: {},
    layout: {},
  });
  const [submitError, setSubmitError] = useState('');
  const [scopeError, setScopeError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [scope, setScope] = useState('current');
  const [selectedSalonIds, setSelectedSalonIds] = useState([
    Number(salonId),
  ]);
  const [pendingBulkSave, setPendingBulkSave] = useState(null);
  const [retryIntent, setRetryIntent] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [decision, setDecision] = useState(null);
  const [viewMode, setViewMode] = useState('essential');
  const [mobileView, setMobileView] = useState('controls');
  const [expandedAdvancedGroup, setExpandedAdvancedGroup] = useState('buttons');
  const [expandedStyleSection, setExpandedStyleSection] = useState(null);
  const [focusTarget, setFocusTarget] = useState('');
  const fieldRefs = useRef({});
  const hydratedSalonIdRef = useRef(null);
  const hydratedResolvedRef = useRef(null);
  const decisionResetResultRef = useRef(null);
  const decisionFocusTargetRef = useRef('');

  const targets = useMemo(
    () => targetsQuery.data?.targets || [],
    [targetsQuery.data],
  );

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(baseline),
    [draft, baseline],
  );

  const registerFieldRef = (field, element) => {
    if (element) fieldRefs.current[field] = element;
  };

  const revealFieldErrors = (errors) => {
    const flattenedErrors = {
      ...(errors.template_key ? { template_key: errors.template_key } : {}),
      ...(errors.theme || {}),
      ...flattenFieldErrors(errors.layout),
    };
    const firstField = FIELD_ORDER.find((field) => flattenedErrors[field]);
    if (!firstField) return;
    const isEssentialField = firstField === 'template_key'
      || SALON_THEME_COLOR_FIELDS.includes(firstField)
      || firstField === 'font_key';
    setViewMode(isEssentialField ? 'essential' : 'advanced');
    setMobileView('controls');
    if (!isEssentialField) {
      setExpandedAdvancedGroup(getAdvancedGroupForField(firstField));
    }
    if (firstField.startsWith('section_styles.')) {
      setExpandedStyleSection(firstField.split('.')[1]);
    }
    setFocusTarget(firstField);
  };

  useLayoutEffect(() => {
    if (!focusTarget) return;
    let timeout;
    let attempts = 0;
    const focusWhenReady = () => {
      const target = fieldRefs.current[focusTarget];
      if (target) {
        target.focus();
        setFocusTarget('');
        return;
      }
      attempts += 1;
      if (attempts < 5) {
        timeout = window.setTimeout(focusWhenReady, 0);
      }
    };
    timeout = window.setTimeout(focusWhenReady, 0);
    return () => window.clearTimeout(timeout);
  }, [
    expandedAdvancedGroup,
    expandedStyleSection,
    fieldErrors,
    focusTarget,
    viewMode,
  ]);

  useEffect(() => {
    const resolved = appearanceQuery.data?.resolved;
    if (!resolved) return;
    const salonKey = Number(salonId);
    const salonChanged = hydratedSalonIdRef.current !== salonKey;
    const responseChanged = hydratedResolvedRef.current !== resolved;
    if (!salonChanged && (dirty || !responseChanged)) return;

    const next = cloneAppearance(resolved);
    setDraft(next);
    setBaseline(cloneAppearance(next));
    hydratedSalonIdRef.current = salonKey;
    hydratedResolvedRef.current = resolved;
  }, [appearanceQuery.data, dirty, salonId]);

  useEffect(() => {
    setScope('current');
    setSelectedSalonIds([Number(salonId)]);
    setPendingBulkSave(null);
    setRetryIntent(null);
    setConfirmationOpen(false);
    setDecision(null);
    setFieldErrors({ template_key: '', theme: {}, layout: {} });
    setSubmitError('');
    setScopeError('');
    setSuccessMessage('');
    setViewMode('essential');
    setMobileView('controls');
    setExpandedAdvancedGroup('buttons');
    setExpandedStyleSection(null);
    setFocusTarget('');
  }, [salonId]);

  useEffect(() => {
    const currentId = Number(salonId);
    const eligibleIds = new Set(
      targets.map(({ salon_id: targetId }) => Number(targetId)),
    );
    const currentIsEligible = eligibleIds.has(currentId);
    if (!currentIsEligible || targets.length < 2) {
      setScope('current');
      setSelectedSalonIds([currentId]);
      return;
    }
    setSelectedSalonIds((current) => (
      [...new Set([
        currentId,
        ...current.map(Number).filter((targetId) => eligibleIds.has(targetId)),
      ])].sort((left, right) => left - right)
    ));
  }, [salonId, targets]);

  const setThemeField = (field, value) => {
    setDraft((current) => ({
      ...current,
      theme: { ...current.theme, [field]: value },
    }));
    setFieldErrors((current) => {
      const theme = { ...current.theme };
      delete theme[field];
      return { ...current, theme };
    });
    setSubmitError('');
    setSuccessMessage('');
    setPendingBulkSave(null);
    setRetryIntent(null);
  };

  const clearDraftFeedback = () => {
    setFieldErrors({ template_key: '', theme: {}, layout: {} });
    setSubmitError('');
    setScopeError('');
    setSuccessMessage('');
    setPendingBulkSave(null);
    setRetryIntent(null);
  };

  const selectTemplate = (templateKey) => {
    if (templateKey === draft.template_key) return;
    setDraft(createSalonTemplateAppearance(templateKey));
    clearDraftFeedback();
  };

  const handleCancelChanges = () => {
    if (!dirty) return;
    setDecision('discard');
  };

  const confirmCancelChanges = () => {
    decisionFocusTargetRef.current = 'template_key';
    setDraft(cloneAppearance(baseline));
    setFieldErrors({ template_key: '', theme: {}, layout: {} });
    setSubmitError('');
    setScopeError('');
    setSuccessMessage('');
    setRetryIntent(null);
    setScope('current');
    setSelectedSalonIds([Number(salonId)]);
    setViewMode('essential');
    setMobileView('controls');
    setExpandedAdvancedGroup('buttons');
    setExpandedStyleSection(null);
    setPendingBulkSave(null);
    setRetryIntent(null);
    setDecision(null);
  };

  const setLayoutField = (section, field, value) => {
    setDraft((current) => ({
      ...current,
      layout: {
        ...current.layout,
        [section]: { ...current.layout[section], [field]: value },
      },
    }));
    setFieldErrors((current) => {
      const sectionErrors = { ...(current.layout?.[section] || {}) };
      delete sectionErrors[field];
      return {
        ...current,
        layout: { ...current.layout, [section]: sectionErrors },
      };
    });
    setSubmitError('');
    setSuccessMessage('');
    setPendingBulkSave(null);
    setRetryIntent(null);
  };

  const setSectionStyleField = (sectionKey, role, field, value) => {
    setDraft((current) => ({
      ...current,
      layout: {
        ...current.layout,
        section_styles: {
          ...current.layout.section_styles,
          [sectionKey]: {
            ...current.layout.section_styles[sectionKey],
            [role]: {
              ...current.layout.section_styles[sectionKey][role],
              [field]: value,
            },
          },
        },
      },
    }));
    setFieldErrors((current) => {
      const sectionStyles = { ...(current.layout?.section_styles || {}) };
      const sectionErrors = { ...(sectionStyles[sectionKey] || {}) };
      const roleErrors = { ...(sectionErrors[role] || {}) };
      delete roleErrors[field];
      sectionErrors[role] = roleErrors;
      sectionStyles[sectionKey] = sectionErrors;
      return {
        ...current,
        layout: { ...current.layout, section_styles: sectionStyles },
      };
    });
    setSubmitError('');
    setSuccessMessage('');
    setPendingBulkSave(null);
    setRetryIntent(null);
  };

  const resetSectionStyle = (sectionKey) => {
    setDraft((current) => ({
      ...current,
      layout: {
        ...current.layout,
        section_styles: {
          ...current.layout.section_styles,
          [sectionKey]: {
            title: { ...DEFAULT_SALON_TITLE_STYLE },
            content: { ...DEFAULT_SALON_CONTENT_STYLE },
          },
        },
      },
    }));
    setFieldErrors((current) => {
      const sectionStyles = { ...(current.layout?.section_styles || {}) };
      delete sectionStyles[sectionKey];
      return {
        ...current,
        layout: { ...current.layout, section_styles: sectionStyles },
      };
    });
    setSubmitError('');
    setSuccessMessage('');
    setPendingBulkSave(null);
    setRetryIntent(null);
  };

  const normalizeField = (field) => {
    const value = draft.theme[field];
    if (typeof value === 'string') {
      const normalizedValue = value.toUpperCase();
      if (normalizedValue !== value) setThemeField(field, normalizedValue);
    }
  };

  const applyServerState = async (state) => {
    const next = cloneAppearance(state?.resolved || createDefaultAppearance());
    setDraft(next);
    setBaseline(cloneAppearance(next));
    setFieldErrors({ template_key: '', theme: {}, layout: {} });
    setScopeError('');
    setSubmitError('');
    setRetryIntent(null);
    await appearanceQuery.refetch?.();
  };

  const executeResetSaloone = async () => {
    const signatureAppearance = createSalonTemplateAppearance(
      DEFAULT_SALON_PUBLIC_TEMPLATE_KEY,
    );
    setSubmitError('');
    setScopeError('');
    setSuccessMessage('');
    setRetryIntent(null);
    try {
      const state = await resetAppearance({
        salonId,
        ...signatureAppearance,
      }).unwrap();
      return {
        state,
        feedback: {
          severity: 'success',
          message: t('salon_theme.success.reset'),
        },
      };
    } catch (error) {
      setRetryIntent('reset');
      return {
        state: null,
        feedback: {
          severity: 'error',
          message: t('salon_theme.errors.reset'),
        },
      };
    }
  };

  const handleResetSaloone = () => {
    setPendingBulkSave(null);
    setDecision('reset');
  };

  const confirmResetSaloone = async () => {
    decisionResetResultRef.current = await executeResetSaloone();
    setDecision(null);
  };

  const publishFeedback = (feedback) => {
    if (!feedback) return;
    if (feedback.severity === 'error') {
      setSuccessMessage('');
      setSubmitError(feedback.message);
      return;
    }
    setSubmitError('');
    setSuccessMessage(feedback.message);
  };

  const handleDecisionExited = async () => {
    const resetResult = decisionResetResultRef.current;
    decisionResetResultRef.current = null;
    if (resetResult?.state) await applyServerState(resetResult.state);
    publishFeedback(resetResult?.feedback);
    const focusTarget = decisionFocusTargetRef.current;
    decisionFocusTargetRef.current = '';
    if (focusTarget) {
      window.setTimeout(() => fieldRefs.current[focusTarget]?.focus(), 0);
    }
  };

  const handleRequestError = (error, fallbackKey = 'salon_theme.errors.network') => {
    if (error?.status === 400 && error.data && typeof error.data === 'object') {
      const themeErrors = error.data.theme
        && typeof error.data.theme === 'object'
        ? error.data.theme
        : error.data;
      const nextThemeErrors = Object.fromEntries(
        Object.entries(themeErrors)
          .filter(([field]) => (
            SALON_THEME_COLOR_FIELDS.includes(field) || field === 'font_key'
          ))
          .map(([field, value]) => [
            field,
            firstError(value),
          ]),
      );
      const layoutErrors = error.data.layout
        && typeof error.data.layout === 'object'
        ? Object.fromEntries(
          [
            'buttons',
            'header',
            'content',
            'visibility',
            'section_styles',
          ].flatMap((section) => {
            const sectionErrors = error.data.layout[section];
            if (!sectionErrors || typeof sectionErrors !== 'object') return [];
            const knownFields = Object.keys(
              DEFAULT_SALON_PUBLIC_LAYOUT[section] || {},
            );
            const fieldEntries = Object.entries(sectionErrors).filter(([
              field,
            ]) => knownFields.includes(field));
            return fieldEntries.length
              ? [[section, Object.fromEntries(fieldEntries)]]
              : [];
          }),
        )
        : {};
      const nonFieldError = findNonFieldError(error.data);
      const nextFieldErrors = {
        template_key: firstError(error.data.template_key) || '',
        theme: nextThemeErrors,
        layout: layoutErrors,
      };
      if (
        nextFieldErrors.template_key
        || Object.keys(nextThemeErrors).length
        || Object.keys(layoutErrors).length
      ) {
        setFieldErrors(nextFieldErrors);
        revealFieldErrors(nextFieldErrors);
      }
      if (error.data.salon_ids) {
        setScopeError(firstError(error.data.salon_ids));
      }
      if (nonFieldError) {
        setSubmitError(nonFieldError);
      }
      if (
        Object.keys(nextThemeErrors).length
        || Object.keys(layoutErrors).length
        || nextFieldErrors.template_key
        || error.data.salon_ids
        || nonFieldError
      ) {
        return;
      }
      setSubmitError(t('salon_theme.errors.validation'));
      return;
    }
    setSubmitError(t(fallbackKey));
  };

  const executeSave = async (appearance, salonIds) => {
    const isBulk = salonIds.length > 1;
    setSubmitError('');
    setScopeError('');
    setSuccessMessage('');
    setRetryIntent(null);
    try {
      const state = isBulk
        ? await applyAppearance({
          salonId,
          template_key: appearance.template_key,
          theme: appearance.theme,
          layout: appearance.layout,
          salonIds,
        }).unwrap()
        : await updateAppearance({
          salonId,
          template_key: appearance.template_key,
          theme: appearance.theme,
          layout: appearance.layout,
        }).unwrap();
      await applyServerState(state);
      setPendingBulkSave(null);
      if (isBulk) {
        setSuccessMessage(t('salon_theme.success.bulk', {
          count: state.updated_count,
        }));
        setScope('current');
        setSelectedSalonIds([Number(salonId)]);
      } else {
        setSuccessMessage(t('salon_theme.success.current'));
      }
    } catch (error) {
      handleRequestError(error);
    }
  };

  const handleSave = () => {
    setRetryIntent(null);
    const themeValidation = validateSalonPublicThemeDraft(draft.theme);
    const layoutValidation = validateSalonPublicLayoutDraft(draft.layout);
    if (!themeValidation.isValid || !layoutValidation.isValid) {
      const nextThemeErrors = Object.fromEntries(
        Object.entries(themeValidation.errors || {}).map(([field, value]) => [
          field,
          firstError(value),
        ]),
      );
      const nextLayoutErrors = Object.fromEntries(
        Object.entries(layoutValidation.errors || {}).map(([
          section,
          sectionErrors,
        ]) => [
          section,
          typeof sectionErrors === 'object' && sectionErrors
            ? Object.fromEntries(Object.entries(sectionErrors).map(([
              field,
              value,
            ]) => [field, firstError(value)]))
            : {},
        ]),
      );
      const nextFieldErrors = {
        template_key: '',
        theme: nextThemeErrors,
        layout: nextLayoutErrors,
      };
      setFieldErrors(nextFieldErrors);
      revealFieldErrors(nextFieldErrors);
      return;
    }

    const appearance = {
      template_key: draft.template_key,
      theme: { ...themeValidation.value },
      layout: cloneLayout(layoutValidation.value),
    };
    setDraft(cloneAppearance(appearance));
    setSubmitError('');
    setScopeError('');
    setSuccessMessage('');
    const salonIds = resolveSalonThemeTargetIds({
      scope,
      currentSalonId: salonId,
      targets,
      selectedSalonIds,
    });
    if (salonIds.length > 1) {
      setPendingBulkSave({ ...cloneAppearance(appearance), salonIds });
      setConfirmationOpen(true);
      return;
    }
    setPendingBulkSave(null);
    executeSave(appearance, salonIds);
  };

  const handleConfirmBulkSave = () => {
    if (!pendingBulkSave) {
      return;
    }
    setConfirmationOpen(false);
    executeSave(pendingBulkSave, pendingBulkSave.salonIds);
  };

  const handleCancelBulkSave = () => {
    setConfirmationOpen(false);
    setPendingBulkSave(null);
  };

  const handleRetry = async () => {
    if (retryIntent === 'reset') {
      const resetResult = await executeResetSaloone();
      if (resetResult.state) await applyServerState(resetResult.state);
      publishFeedback(resetResult.feedback);
      return;
    }
    if (pendingBulkSave) {
      executeSave(pendingBulkSave, pendingBulkSave.salonIds);
      return;
    }
    handleSave();
  };

  const handleScopeChange = (nextScope) => {
    setScope(nextScope);
    setScopeError('');
    setSubmitError('');
    setSuccessMessage('');
    setPendingBulkSave(null);
    setRetryIntent(null);
  };

  const handleSelectedSalonIdsChange = (nextSalonIds) => {
    setSelectedSalonIds(nextSalonIds);
    setScopeError('');
    setSubmitError('');
    setSuccessMessage('');
    setPendingBulkSave(null);
    setRetryIntent(null);
  };

  if (appearanceQuery.isLoading && !appearanceQuery.data) {
    return (
      <Box role="status" aria-label={t('salon_theme.loading')} sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isSubmitting = Boolean(
    updateState.isLoading || applyState.isLoading || resetState.isLoading,
  );
  const direction = typeof i18n.dir === 'function'
    ? i18n.dir()
    : i18n.language === 'ar' ? 'rtl' : 'ltr';
  const pendingTargetNames = pendingBulkSave?.salonIds.map((targetId) => (
    targets.find(
      ({ salon_id: candidateId }) => Number(candidateId) === Number(targetId),
    )?.name || (Number(targetId) === Number(salonId) ? salonName : String(targetId))
  )) || [];
  const supportsStructuralControls = getSalonPublicTemplate(
    draft.template_key,
  ).capabilities.structuralControls;

  return (
    <>
      <SalonThemeStudioLayout
        mobileView={mobileView}
        onMobileViewChange={setMobileView}
        controlsTitle={(
          <Stack spacing={0.5}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 800, textAlign: 'start' }}>
              {t('salon_theme.title_simplified')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('salon_theme.description_simplified')}
            </Typography>
          </Stack>
        )}
        previewTitle={(
          <Typography variant="h6" component="h3" sx={{ fontWeight: 800, textAlign: 'start' }}>
            {t('salon_theme.preview.title')}
          </Typography>
        )}
        controls={(
          viewMode === 'essential' ? (
            <SalonThemeEssentialControls
              appearance={draft}
              errors={fieldErrors}
              disabled={isSubmitting}
              onTemplateChange={selectTemplate}
              onThemeFieldChange={setThemeField}
              onThemeFieldBlur={normalizeField}
              onOpenAdvanced={() => setViewMode('advanced')}
              registerFieldRef={registerFieldRef}
            />
          ) : (
            <SalonThemeAdvancedControls
              appearance={draft}
              capabilities={{ structuralControls: supportsStructuralControls }}
              errors={fieldErrors.layout}
              expandedGroup={expandedAdvancedGroup}
              onExpandedGroupChange={setExpandedAdvancedGroup}
              onBack={() => setViewMode('essential')}
              onLayoutFieldChange={setLayoutField}
              onSectionStyleFieldChange={setSectionStyleField}
              onResetSection={resetSectionStyle}
              onResetSaloone={handleResetSaloone}
              expandedStyleSection={expandedStyleSection}
              onExpandedStyleSectionChange={setExpandedStyleSection}
              disabled={isSubmitting}
              registerFieldRef={registerFieldRef}
            />
          )
        )}
        mobilePreviewSummary={(
          <SalonThemeMobilePreviewSummary
            onOpenPreview={() => setMobileView('preview')}
          >
            <SalonThemePreview
              appearance={draft}
              viewModel={previewData.viewModel}
              isLoading={previewData.isLoading}
              isError={previewData.isError}
              direction={direction}
              viewport="mobile"
            />
          </SalonThemeMobilePreviewSummary>
        )}
        preview={({ isMobile }) => (
          <SalonThemeViewportToggle mobileContext={isMobile}>
            {(viewport) => (
              <SalonThemePreview
                appearance={draft}
                viewModel={previewData.viewModel}
                isLoading={previewData.isLoading}
                isError={previewData.isError}
                direction={direction}
                viewport={viewport}
              />
            )}
          </SalonThemeViewportToggle>
        )}
        feedback={(
          <Stack spacing={1}>
            {targets.length > 1 && (
              <Typography variant="body2" color="text.secondary">
                {t('salon_theme.apply_scope.local_actions_help')}
              </Typography>
            )}
          </Stack>
        )}
        footer={({ isMobile, compactActions }) => (
          <SalonThemeActionBar
            dirty={dirty}
            disabled={isSubmitting}
            mobile={isMobile || compactActions}
            accentColor={draft.theme.primary_color}
            scopeProps={{
              scope,
              onScopeChange: handleScopeChange,
              targets,
              currentSalonId: salonId,
              selectedSalonIds,
              onSelectedSalonIdsChange: handleSelectedSalonIdsChange,
              error: scopeError,
            }}
            onCancelChanges={handleCancelChanges}
            onSave={handleSave}
          />
        )}
      />

      <SalonThemeDecisionDialog
        open={Boolean(decision)}
        title={decision ? t(`salon_theme.confirmations.${decision}.title`) : ''}
        description={decision ? t(`salon_theme.confirmations.${decision}.body`) : ''}
        cancelLabel={decision ? t(`salon_theme.confirmations.${decision}.cancel`) : ''}
        confirmLabel={decision ? t(`salon_theme.confirmations.${decision}.confirm`) : ''}
        loading={decision === 'reset' && resetState.isLoading}
        direction={direction}
        onCancel={() => {
          if (!isSubmitting) setDecision(null);
        }}
        onConfirm={decision === 'discard' ? confirmCancelChanges : confirmResetSaloone}
        onExited={handleDecisionExited}
      />

      <SalonThemeFeedbackToast
        open={Boolean(submitError || successMessage)}
        message={submitError || successMessage}
        severity={submitError ? 'error' : 'success'}
        actionLabel={submitError ? t('salon_theme.actions.retry') : ''}
        closeLabel={t('common.close')}
        direction={direction}
        onAction={handleRetry}
        onClose={() => {
          setSubmitError('');
          setSuccessMessage('');
        }}
      />

      <Dialog
        open={confirmationOpen}
        onClose={handleCancelBulkSave}
        aria-describedby="salon-theme-bulk-confirm-description"
      >
        <DialogTitle>{t('salon_theme.bulk_confirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="salon-theme-bulk-confirm-description">
            {t('salon_theme.bulk_confirm.body', {
              count: pendingTargetNames.length,
            })}
          </DialogContentText>
          <List dense>
            {pendingTargetNames.map((targetName) => (
              <ListItem key={targetName} disableGutters>
                <ListItemText primary={targetName} />
              </ListItem>
            ))}
          </List>
          <Alert severity="warning">
            {t('salon_theme.bulk_confirm.overwrite_warning')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelBulkSave} disabled={isSubmitting}>
            {t('salon_theme.bulk_confirm.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmBulkSave}
            disabled={isSubmitting}
          >
            {t('salon_theme.bulk_confirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SalonThemeEditor;
